import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import {
  DraggableModelessDialogOrdersChangeAction,
  DraggableModelessDialogOrdersContext,
  DraggableModelessDialogOrdersDispatchContext,
} from './DraggableModelessDialogsProvider';

interface Props {
  /** このダイアログの表示状態。 */
  isOpen?: boolean | undefined;
  /** このダイアログが閉じられる際に呼ばれる関数。 */
  onClose?: () => void | undefined;
  /** このダイアログの内容。 */
  children: React.ReactNode;

  /** このダイアログのヘッダーに表示する要素。 */
  headerContents?: React.ReactNode | undefined;

  /** 初期値となる width (px)。 */
  initialWidth?: number | undefined;
  /** 初期値となる height (px)。 */
  initialHeight?: number | undefined;

  /** 表示中の全ての DraggableModelessDialogs の中で一意の ID。先祖に DraggableModelessDialogsProvider がいる場合に、表示順序を管理するために使用する。 */
  id?: number | string | undefined;
}

/**
 * ドラッグで動かせるモードレス ダイアログ。複数表示する場合、DraggableModelessDialogsProvider で囲むことで表示順序を管理可能。
 */
function DraggableModelessDialog({
  isOpen,
  onClose,
  children,
  headerContents,
  initialWidth,
  initialHeight,
  id,
}: Props) {
  /** このモーダルの表示状態。prop で指定されていなければデフォルトで true。 */
  const [isOpenState, setIsOpenState] = useState<boolean>(typeof isOpen === 'boolean' ? isOpen : true);

  useEffect(() => {
    if (typeof isOpen === 'boolean') {
      // prop が更新されたら state に反映。
      setIsOpenState(isOpen);
    }
  }, [isOpen]);

  /** × ボタン押下時に実行される関数。prop で指定されていなければ、モーダルを閉じる動作のみ行う。 */
  const onCloseFunction =
    typeof onClose === 'function'
      ? onClose
      : () => {
          setIsOpenState(false);
        };

  /** このダイアログの width (px)。リサイズで使用。 */
  const [width, setWidth] = useState<number>(typeof initialWidth === 'number' ? initialWidth : 800);
  /** このダイアログの height (px)。リサイズで使用。 */
  const [height, setHeight] = useState<number>(typeof initialHeight === 'number' ? initialHeight : 400);

  //* MEMO: 左下のリサイズは、リサイズ用 Draggable がドラッグされる (その差異は deltaX (sizeDeltaX)) と同時に、ダイアログの x も変わるためか、左下 Draggable が左右に激しく揺れる (position の設定がおかしいのか)。原因究明と対応が面倒なので、とりあえず左下のリサイズは実装しない。Draggable を使用する方法ではなく、もっといい手段があるとは思うが。

  // // ↓ 左下のリサイズに対応するために、Draggable の position を設定する必要がある。
  // /** ダイアログをドラッグする際の新しい x。 */
  // const [dialogX, setDialogX] = useState<number>(0);
  // const [dialogDeltaX, setDialogDeltaX] = useState<number>(0);
  // /** ダイアログをドラッグする際の新しい y。 */
  // const [dialogY, setDialogY] = useState<number>(0);

  /** リサイズする際の X 軸の動いた px 数。 */
  const [sizeDeltaX, setSizeDeltaX] = useState<number>(0);
  /** リサイズする際の Y 軸の動いた px 数。 */
  const [sizeDeltaY, setSizeDeltaY] = useState<number>(0);

  // const [clientX, setClientX] = useState<number>(0);
  // const [clientY, setClientY] = useState<number>(0);

  /** 表示順序。DraggableModelessDialogsProvider の Context を使用して、表示中の全ての DraggableModelessDialogs の中で、このダイアログがどの位置にいるかを判別するために使用する。 */
  const orders = useContext(DraggableModelessDialogOrdersContext);
  /** 表示順序変更用関数。 */
  const dispatch = useContext(DraggableModelessDialogOrdersDispatchContext);

  // console.log('orders:', orders);
  // console.log('dispatch:', dispatch);

  /** このダイアログを最前面に移動させる関数。 */
  const moveToForefront = useCallback(() => {
    if (typeof dispatch === 'function') {
      // このダイアログの ID を順序の配列のトップに置く。
      (dispatch as CallableFunction)({
        type: 'changed',
        id,
        order: 0,
      } as DraggableModelessDialogOrdersChangeAction);
    }
  }, [dispatch, id]);

  /** このダイアログの ID を表示順序の配列から削除する関数。 */
  const deleteFromOrders = useCallback(() => {
    if (typeof dispatch === 'function') {
      // このダイアログの ID を順序の配列から削除する。
      (dispatch as CallableFunction)({
        type: 'deleted',
        id,
      } as DraggableModelessDialogOrdersChangeAction);
    }
  }, [dispatch, id]);

  // useEffect() 内で比較するために使用。以前の isOpenState の値を保存。
  const [previousIsOpenState, setPreviousIsOpenState] = useState<boolean>(!isOpenState);

  useEffect(() => {
    if (!previousIsOpenState && isOpenState) {
      // ダイアログが表示された。
      moveToForefront();
    }
    if (previousIsOpenState && !isOpenState) {
      // ダイアログが非表示になった。
      deleteFromOrders();
    }

    if (previousIsOpenState !== isOpenState) {
      // 変更された表示状態を保存。
      setPreviousIsOpenState(isOpenState);
    }
  }, [deleteFromOrders, dispatch, id, isOpenState, moveToForefront, previousIsOpenState]);

  useEffect(
    () =>
      // クリーン アップ関数。
      () => {
        // console.log('cleanup!!');
        // アンマウントされた。
        deleteFromOrders();
      },
    // アンマウント時に呼ぶ。
    [deleteFromOrders]
  );

  // 先祖に Provider がいて (Context に囲まれていて) 既に登録されていれば、順序を取得。
  const order =
    orders && (typeof id === 'number' || typeof id === 'string') && orders.includes(id) ? orders.indexOf(id) : 0;

  // https://github.com/react-grid-layout/react-draggable/blob/v4.4.2/lib/DraggableCore.js#L159-L171
  /** このダイアログ全体の Draggable と div 用 ref。 */
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // /** フッター左のリサイズ用 Draggable と div 用 ref。 */
  // const footerLeftDivRef = useRef<HTMLDivElement | null>(null);
  /** フッター右のリサイズ用 Draggable と div 用 ref。 */
  const footerRightDivRef = useRef<HTMLDivElement | null>(null);

  /** 初期値となるLeftを画面中央に設定 */
  const left = (window.innerWidth - width) / 2;

  return (
    <>
      <Draggable
        nodeRef={dialogRef}
        handle=".header"
        onStart={() => {
          // ドラッグが始まった。
          // 最前面に移動させる。
          moveToForefront();
        }}
        // onDrag={
        //   (event: DraggableEvent, data: DraggableData) => {
        //     // position に渡すために保存。
        //     setDialogX(data.x);
        //     setDialogY(data.y);

        //     setDialogDeltaX(data.deltaX);

        //     console.log('sizeDeltaX:', sizeDeltaX);
        //     console.log('dialogX:', data.x);

        //   }
        // }
        // 左下からのリサイズはダイアログの x を同時に変える必要がある。ダイアログ動かす用の Draggable の position にリサイズ用の deltaX とドラッグ用の deltaX を足せばいけるか？
        // position={{
        //   x: dialogDeltaX + sizeDeltaX,
        //   y: dialogY,
        // }}
      >
        <div
          className="draggable-ckeditor-dialog"
          ref={dialogRef}
          style={{
            visibility: isOpenState ? 'visible' : 'hidden',
            // display: (isOpenState) ? 'initial' : 'none',
            backgroundColor: 'white',
            border: '2px solid lightgray',
            borderRadius: '1em',
            // overflow: 'hidden',
            position: 'fixed',
            top: '5em',
            left: `${left}px`,
            maxWidth: '90vw',
            maxHeight: '90vh',
            minHeight: '40px',
            width: `${width}px`,
            height: `${height}px`,
            // zIndex: (typeof order === 'number') ? (1000 - order) : 100,
            zIndex: 1000 - order,
          }}
          onFocus={() => {
            // フォーカスされた。
            // 最前面に移動させる。
            moveToForefront();
          }}
        >
          {/* <div
          style={{
            width: `${width - 6}px`,
            height: `${height - 6}px`,
            // overflow: 'auto',
            position: 'relative',
            top: '2px',
            left: '2px',
            cursor: 'wait',
          }}
        > */}
          {/* ヘッダー。 */}
          <div
            style={{
              height: '20px',
              cursor: 'move',
              display: 'flex',
              justifyContent: headerContents ? 'space-between' : 'flex-end',
              borderBottom: '1px solid lightgray',
            }}
            className="header"
            onClick={() => {
              // ヘッダーがクリックされた。
              // 最前面に移動させる。
              moveToForefront();
            }}
          >
            {/* prop で渡されてきた、ヘッダーに表示する要素。 */}
            {headerContents}
            {/* 閉じるボタン。 */}
            <button
              type="button"
              onClick={onCloseFunction}
              style={{
                // marginRight: '0.5em',
                // marginLeft: 'auto',
                background: '#666666',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                width: '20px',
                height: '20px',
                paddingBottom: '3px',
                fontSize: '27px',
                lineHeight: '0',
                borderRadius: '0% 40% 0% 0%',
              }}
            >
              ×
            </button>
          </div>
          {/* children を囲む div。 */}
          <div
            style={{
              overflow: 'auto',
              // width: `${width}px`,
              height: `${height - 40}px`,
              // borderBottomLeftRadius: '1em',
              // borderBottomRightRadius: '1em',
            }}
          >
            {children}
          </div>
          {/* フッター。リサイズ用。(ブラウザーによっては children を囲む div ↑ のスクロール バーが角ばっているので、それが一番下に来た場合、このダイアログの角が丸い場合そこだけおかしくなるので、見た目上の緩衝のためでもある) */}
          <div
            style={{
              height: '20px',
              borderTop: '1px solid lightgray',
              display: 'flex',
              justifyContent: 'flex-end',
              // justifyContent: 'space-between',
            }}
            onClick={() => {
              // フッターがクリックされた。
              // 最前面に移動させる。
              moveToForefront();
            }}
          >
            {/* <Draggable
            nodeRef={footerLeftDivRef}
            handle='.footerLeftDiv'
            onDrag={
              (event: DraggableEvent, data: DraggableData) => {
                // position に渡すために保存。
                setSizeDeltaX(data.deltaX);
                setSizeDeltaY(data.deltaY);

                console.log('sizeDeltaX:', data.deltaX);


                // リサイズ。
                // 左下のリサイズは width の +- が逆になる。
                setWidth((previousWidth: number) => previousWidth - data.deltaX);
                setHeight((previousHeight: number) => previousHeight + data.deltaY);
              }
            }
            // リサイズとドラッグが重なると本来の位置から離れていってしまうので、位置を deltaX と deltaY にすることで、大体元々の位置に収まるようにする (実はちょっとずれることがある)。
            position={{
              x: sizeDeltaX,
              y: sizeDeltaY,
            }}
          >
            <div
              ref={footerLeftDivRef}
              className='footerLeftDiv'
              style={{
                // borderLeft: '1px solid lightgray',
                backgroundColor: 'red',
                width: '20px',
                height: '20px',
                borderBottomLeftRadius: '1em',
                cursor: 'nesw-resize',
              }}
            />
          </Draggable> */}

            {/* リサイズ用 Draggable。別に Draggable でなくてもよいが、手早くこれで実装してみた。 */}
            <Draggable
              nodeRef={footerRightDivRef}
              handle=".footerRightDiv"
              // onMouseDown={
              //   (event: MouseEvent) => {
              //     console.log('event.clientX:', event.clientX);
              //     console.log('event.clientY:', event.clientY);

              //     setClientX(event.clientX);
              //     setClientY(event.clientY);
              //   }
              // }
              onDrag={(event: DraggableEvent, data: DraggableData) => {
                // console.log('data:', data);

                // position に渡すために保存。
                setSizeDeltaX(data.deltaX);
                setSizeDeltaY(data.deltaY);

                // setClientX((previousClientX: number) => previousClientX + data.deltaX);
                // setClientY((previousClientY: number) => previousClientY + data.deltaY);

                // リサイズ。
                setWidth((previousWidth: number) => previousWidth + data.deltaX);
                setHeight((previousHeight: number) => previousHeight + data.deltaY);
              }}
              // リサイズとドラッグが重なると本来の位置から離れていってしまうので、位置を deltaX と deltaY にすることで、大体元々の位置に収まるようにする (実はちょっとずれることがある)。
              position={{
                x: sizeDeltaX,
                y: sizeDeltaY,
              }}
              // bounds={{ left: 10, top: 10, right: 10, bottom: 10, }}
            >
              {/* リサイズ用 div。 */}
              <div
                ref={footerRightDivRef}
                className="footerRightDiv"
                style={{
                  // borderLeft: '1px solid lightgray',
                  // backgroundColor: 'red',
                  width: '20px',
                  height: '20px',
                  borderBottomRightRadius: '1em',
                  cursor: 'nwse-resize',
                  // zIndex: 1000,
                }}
                // onMouseDown={
                //   (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                //     console.log('onMouseDown:', event);
                //     setClientX(event.clientX);
                //     setClientY(event.clientY);
                //   }
                // }
                // onMouseMove={
                //   (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                //     console.log('onMouseMove:', event);

                //   }
                // }
              />
            </Draggable>
          </div>
          {/* </div> */}
        </div>
      </Draggable>
      {!headerContents && (
        <div
          onClick={onCloseFunction}
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100vw',
            height: '100vh',
            zIndex: 100 - order,
          }}
        />
      )}
    </>
  );
}

DraggableModelessDialog.defaultProps = {
  isOpen: undefined,
  onClose: undefined,
  headerContents: undefined,
  initialWidth: undefined,
  initialHeight: undefined,
  id: undefined,
};

export default DraggableModelessDialog;
